/**
 * Soft per-property context captured during onboarding / install.
 * Bridges business goal + detected platform into CreateExperiment until
 * the API can persist these on the property.
 */

import type { OnboardingGoalId, OnboardingIcpId } from "@/lib/types/onboarding-goal"
import { isOnboardingGoalId, isOnboardingIcpId } from "@/lib/types/onboarding-goal"
import type { PlatformId } from "@/lib/types/platform"
import { normalizePlatformId } from "@/lib/utils/detectPlatform"

const KEY_PREFIX = "humblytics:site-context:"
const TTL_MS = 90 * 24 * 60 * 60 * 1000 // 90 days

type SiteContext = {
  propertyId: string
  icp?: OnboardingIcpId | null
  goal?: OnboardingGoalId | null
  platform?: PlatformId | null
  domain?: string | null
  detectedAt?: number
  updatedAt: number
}

function storageKey(propertyId: string): string {
  return `${KEY_PREFIX}${propertyId}`
}

function isFresh(ctx: SiteContext): boolean {
  return Date.now() - ctx.updatedAt < TTL_MS
}

export function getSiteContext(propertyId: string | undefined | null): SiteContext | null {
  if (!propertyId) return null
  try {
    const raw = localStorage.getItem(storageKey(propertyId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as SiteContext
    if (!parsed || typeof parsed !== "object" || parsed.propertyId !== propertyId) {
      return null
    }
    if (!isFresh(parsed)) {
      localStorage.removeItem(storageKey(propertyId))
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function setSiteContext(
  propertyId: string,
  patch: Partial<Omit<SiteContext, "propertyId" | "updatedAt">>,
): SiteContext | null {
  if (!propertyId) return null
  const prev = getSiteContext(propertyId)
  const next: SiteContext = {
    propertyId,
    icp: patch.icp !== undefined ? patch.icp : prev?.icp ?? null,
    goal: patch.goal !== undefined ? patch.goal : prev?.goal ?? null,
    platform: patch.platform !== undefined ? patch.platform : prev?.platform ?? null,
    domain: patch.domain !== undefined ? patch.domain : prev?.domain ?? null,
    detectedAt:
      patch.detectedAt !== undefined
        ? patch.detectedAt
        : patch.platform
          ? Date.now()
          : prev?.detectedAt,
    updatedAt: Date.now(),
  }

  // Normalize inbound values
  if (next.icp != null && !isOnboardingIcpId(next.icp)) {
    next.icp = null
  }
  if (next.goal != null && !isOnboardingGoalId(next.goal)) {
    next.goal = null
  }
  if (next.platform != null) {
    next.platform = normalizePlatformId(next.platform) ?? next.platform
  }

  try {
    localStorage.setItem(storageKey(propertyId), JSON.stringify(next))
  } catch {
    // Best-effort
  }
  return next
}

/** Write goal early (survives a later property attach). */
function setSiteContextGoal(
  propertyId: string,
  goal: OnboardingGoalId | null,
  domain?: string,
): void {
  setSiteContext(propertyId, { goal, domain: domain ?? undefined })
}

export function setSiteContextPlatform(
  propertyId: string,
  platform: PlatformId | string | null,
  domain?: string,
): void {
  const normalized = platform ? normalizePlatformId(platform) : null
  setSiteContext(propertyId, {
    platform: normalized,
    domain: domain ?? undefined,
    detectedAt: Date.now(),
  })
}

/**
 * Fallback: read onboarding goal from generation pending record
 * when site-context was never written (older sessions).
 */
function getGoalFromGenerationPending(
  propertyId: string | undefined | null,
): OnboardingGoalId | null {
  if (!propertyId) return null
  try {
    const raw = localStorage.getItem("humblytics:generation:pending:v1")
    if (!raw) return null
    const record = JSON.parse(raw) as { propertyId?: string; goal?: string }
    if (record.propertyId !== propertyId) return null
    return isOnboardingGoalId(record.goal) ? record.goal : null
  } catch {
    return null
  }
}

export function resolveSiteContext(propertyId: string | undefined | null): SiteContext | null {
  const ctx = getSiteContext(propertyId)
  if (ctx?.goal) return ctx
  const goal = getGoalFromGenerationPending(propertyId)
  if (!goal) return ctx
  if (propertyId) {
    return setSiteContext(propertyId, { goal }) ?? { propertyId, goal, updatedAt: Date.now() }
  }
  return ctx
}
